<template>
  <div class="aca-text-field">
    <!-- TODO: Transform the label to atom when used again in another atom -->
    <!-- TODO: Create a higher abstraction being a molecule containing the Label and the current component -->
    <label
      v-if="label"
      :for="id ? id : null"
      class="mb-1 d-inline-block text text-m-small-regular"
      :class="{
        'grey--text text--lighten-1': disabled,
        'error--text': hasError,
        'grey--text text--darken-3': !hasError && !disabled,
      }"
    >
      {{ label }}
    </label>
    <v-text-field
      :id="id"
      ref="input"
      v-bind="$attrs"
      v-model="inputValue"
      solo
      class="text"
      :class="[isMobile ? 'text-l-large-regular' : 'text-m-large-regular']"
      :disabled="disabled"
      :rules="rules"
      :hide-details="hideDetails"
      :height="localHeight"
      :label="null"
      v-on="$listeners"
      @blur="isDirty = true"
      @keydown.enter.prevent="handleEnterPress"
    >
      <template #append>
        <slot name="append">
          <PhWarningOctagon
            v-if="hasError"
            weight="light"
            size="24"
          />
        </slot>
      </template>
    </v-text-field>
  </div>
</template>

<script>
import { PhWarningOctagon } from 'phosphor-vue';

export default {
  name: 'AcaTextField',
  components: {
    PhWarningOctagon,
  },
  inheritAttrs: false,
  props: {
    id: {
      type: String,
      required: false,
      default: null,
    },
    value: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      required: false,
      default: () => [],
    },
    height: {
      type: String,
      required: false,
      default: null,
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      inputHasErrorFromFormSubmission: false,
      isDirty: false,
    };
  },
  computed: {
    inputValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    isMobile() {
      return this.$breakpoint.mobileAndDown;
    },
    localHeight() {
      if (this.height) return this.height;

      if (this.$breakpoint.mobileAndDown) {
        return '48';
      } else {
        return '42';
      }
    },
    hasError() {
      // This is the case when the error is coming from the form submission
      if (this.inputHasErrorFromFormSubmission) return true;

      // We only show errors on user interaction
      if (!this.isDirty) return false;

      return this.rules?.some((rule) => {
        const appliedRule = typeof rule === 'function' ? rule(this.inputValue) : rule;
        const isValidInput = appliedRule === true;

        return !isValidInput;
      });
    },
  },
  watch: {
    inputValue(newValue, oldValue) {
      this.inputHasErrorFromFormSubmission = false;

      if (this.isDirty) return;

      if (newValue !== oldValue) {
        this.isDirty = true;
      }
    },
  },
  mounted() {
    // We use a MutationObserver to monitor changes to the input element's class list.
    // This allows us to detect errors resulting from form submissions.
    // The Vuetify API doesn't offer a direct way (or I couldn't find one) to handle these errors,
    // and this implementation avoids prop drilling for error handling from each parent component using this atom.
    this.observer = new MutationObserver((mutationList) => {
      for (const mutation of mutationList) {
        const classAttrValue = mutation.target.getAttribute(mutation.attributeName);
        this.$nextTick(() => {
          // error--text is a class that Vuetify adds to the input element when it has an error
          if (classAttrValue.split(' ').includes('error--text')) {
            this.inputHasErrorFromFormSubmission = true;
          } else {
            this.inputHasErrorFromFormSubmission = false;
          }
        });
      }
    });

    this.observer.observe(this.$refs.input.$el, {
      attributes: true,
      attributeFilter: ['class'],
    });
  },
  beforeDestroy() {
    this.observer.disconnect();
  },
  methods: {
    handleEnterPress() {
      this.$emit('on-enter-pressed');
    },
  },
};
</script>

<!-- NOTE: If any styles of this base component change (including base, error, or focus states), 
  update the Stripe Elements appearance configuration (stripeElementsAppearance.js) to match. -->
<style scoped lang="scss">
@import '@/sass/texts.scss';

.aca-text-field {
  // Adjusting line-height to compensate for extra space between the main div and the label
  line-height: calc(1rem - 4px);
  text-align: left;
  width: 100%;
}

.aca-text-field ::v-deep .v-input__slot {
  background: var(--v-card-base) !important;
  box-shadow: inset -3px -3px 3px rgba(255, 255, 255, 0.7), inset 3px 3px 3px rgba(174, 174, 192, 0.2) !important;
  border-radius: 4px;
  min-height: auto !important;
  margin-bottom: 4px;
  padding: 0 16px !important;

  &:hover {
    background: var(--v-background-base) !important;
  }
}

@media #{map-get($display-breakpoints, 'tablet-and-up')} {
  .aca-text-field ::v-deep .v-input__slot {
    padding: 0 12px !important;
  }
}

.aca-text-field ::v-deep .v-input {
  // used by the svg with the currentColor
  color: var(--v-grey-darken1) !important;
}

.aca-text-field ::v-deep .v-text-field__details {
  margin-bottom: 0;
  padding-left: 0;
  // don't reduce this, otherwise you will have layout shift
  // because the line-height of the child .v-messages__message is 1 rem
  min-height: 1rem;
}

.aca-text-field ::v-deep .v-messages {
  min-height: 0px;
}

.aca-text-field ::v-deep .v-messages__message {
  @extend .text, .text-m-small-regular;
}

.aca-text-field ::v-deep input::placeholder {
  color: var(--v-grey-darken1) !important;
}

.aca-text-field ::v-deep input {
  color: var(--v-grey-darken4) !important;
}
</style>

<!-- Disabled styles -->
<style scoped lang="scss">
.aca-text-field ::v-deep .v-input--is-disabled .v-input__slot {
  background: var(--v-grey-lighten3) !important;
  box-shadow: none !important;
}

.aca-text-field ::v-deep .v-input--is-disabled input::placeholder {
  color: var(--v-grey-lighten1) !important;
  opacity: 1;
}
</style>

<!-- Focus state styles -->
<style scoped lang="scss">
.aca-text-field ::v-deep .v-input.v-input--is-focused {
  // used by the svg with the currentColor
  color: var(--v-grey-darken1) !important;
}

.aca-text-field ::v-deep .v-input--is-focused .v-input__slot {
  box-shadow: inset 0 0 0 2px var(--v-secondary-border-base) !important;
  background: var(--v-card-base) !important;
}
</style>

<!-- Error status styles -->
<style scoped lang="scss">
.aca-text-field ::v-deep .error--text.v-input {
  // used by the svg with the currentColor
  color: var(--v-error-base) !important;
}

.aca-text-field .error--text ::v-deep .v-input__slot {
  box-shadow: inset 0 0 0 2px var(--v-error-border-base) !important;
  background: var(--v-error-surface-base) !important;

  &:hover {
    background: var(--v-error-new-hover-base) !important;
  }
}

// TODO: we are mixing error with focus, but we have a block with focus styles
.aca-text-field .error--text.v-input--is-focused ::v-deep .v-input__slot {
  box-shadow: inset 0 0 0 2px var(--v-error-base) !important;
  background: var(--v-error-surface-base) !important;
}

.aca-text-field .error--text ::v-deep .v-input__control input {
  color: var(--v-error-base) !important;
}
</style>
